'use client'

import { useEffect, useRef, useState } from 'react'
import { List, X } from '@phosphor-icons/react'

export interface TOCItem {
  id: string
  label: string
  level?: 2 | 3
}

interface BlogTOCProps {
  items: TOCItem[]
  label?: string
  /** When true, renders as a mobile bottom-sheet instead of an inline sidebar nav. */
  mobileSheet?: boolean
}

export function BlogTOC({ items, label = 'On this page', mobileSheet = false }: BlogTOCProps) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (items.length === 0) return
    const headings = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null)

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    )

    headings.forEach((h) => observer.observe(h))
    return () => observer.disconnect()
  }, [items])

  // Auto-scroll the active TOC item into view within the list
  useEffect(() => {
    if (!activeId || !listRef.current) return
    const link = listRef.current.querySelector<HTMLElement>(`[data-id="${activeId}"]`)
    if (link) link.scrollIntoView({ block: 'nearest' })
  }, [activeId])

  if (items.length === 0) return null

  const scrollToId = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Use window.scrollTo with explicit Y calculation instead of scrollIntoView.
    // scrollIntoView can be routed to a nested overflow container on iOS Safari;
    // window.scrollTo always targets the page scroll position.
    const navEl = document.querySelector('header') as HTMLElement | null
    const navH = navEl ? navEl.getBoundingClientRect().height : 72
    const top = el.getBoundingClientRect().top + window.scrollY - navH - 8
    window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' })
    history.replaceState(null, '', `#${id}`)
  }

  const handleLinkClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    if (mobileSheet) {
      // Close the sheet, then wait for the CSS slide-away transition (200ms)
      // before scrolling. Using setTimeout rather than rAF so the sheet's
      // pointer-events:none is applied before window.scrollTo fires.
      setSheetOpen(false)
      setTimeout(() => scrollToId(id), 150)
    } else {
      scrollToId(id)
    }
  }

  const tocList = (
    <ul className="blog-toc__list" ref={listRef}>
      {items.map((item) => (
        <li key={item.id} className="blog-toc__item">
          <a
            href={`#${item.id}`}
            data-id={item.id}
            className={`blog-toc__link ${item.level === 3 ? 'blog-toc__link--h3' : ''}`}
            aria-current={activeId === item.id ? 'location' : undefined}
            onClick={(e) => handleLinkClick(e, item.id)}
          >
            {item.label}
          </a>
        </li>
      ))}
    </ul>
  )

  if (mobileSheet) {
    return (
      <>
        {/* Floating pill trigger */}
        <button
          type="button"
          className="blog-toc-pill"
          aria-label="Open table of contents"
          aria-expanded={sheetOpen}
          onClick={() => setSheetOpen(true)}
        >
          <List size={16} weight="bold" aria-hidden="true" />
          <span>Contents</span>
        </button>

        {/* Backdrop */}
        {sheetOpen && (
          <div
            className="blog-toc-sheet-backdrop"
            aria-hidden="true"
            onClick={() => setSheetOpen(false)}
          />
        )}

        {/* Drawer */}
        <div
          className={`blog-toc-sheet${sheetOpen ? ' blog-toc-sheet--open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={label}
        >
          <div className="blog-toc-sheet__header">
            <span className="blog-toc__label">{label}</span>
            <button
              type="button"
              className="btn-icon-only btn-icon-only--sm btn-icon-only--outline"
              aria-label="Close table of contents"
              onClick={() => setSheetOpen(false)}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          {tocList}
        </div>
      </>
    )
  }

  return (
    <nav className="blog-toc" aria-label={label}>
      <p className="blog-toc__label">{label}</p>
      {tocList}
    </nav>
  )
}
